// Banner behaviour tab — regulation cards, visibility defaults, guide FAQ.

import { BANNER_REGULATION } from 'src/services/consts/shared.const'

/** `visibleCountries` sentinel when the banner shows in all regions. */
export const VISIBLE_COUNTRIES_ALL = ['*']

/**
 * `visibleCountries` sentinel for a regulation's standard territory.
 * The backend expands this on write; it must never be mixed with explicit codes.
 * Expands to `[]` for `notice` — notice must use `VISIBLE_COUNTRIES_ALL` instead.
 */
export const VISIBLE_COUNTRIES_DEFAULT = ['default']

/**
 * Reference data: the countries each regulation's `['default']` sentinel expands
 * to on the backend. Not sent on the wire — kept here so help-center and guide
 * copy can list the territories. Intentionally unreferenced; do not drop as dead.
 */
export const GDPR_VISIBLE_COUNTRIES = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'IS',
  'LI',
  'NO',
  'GB',
]

/** @see GDPR_VISIBLE_COUNTRIES — reference data, not sent on the wire. */
export const CCPA_VISIBLE_COUNTRIES = ['US']

/**
 * Catch-all owner sentinel. Non-`NONE` values are `BANNER_REGULATION` values —
 * the regulation whose `visibleCountries` entry is `['*']`.
 */
export const CATCH_ALL_OWNER = {
  NONE: 'none',
}

/** Icon for the Rest of the World "No banner" choice. */
export const CATCH_ALL_NONE_ICON = 'circle-off'

/**
 * Info copy when Rest of the World has no owner, or the regulations step is empty.
 */
export const CATCH_ALL_COVERAGE_MESSAGE = {
  CHOOSE: 'Choose what visitors outside your selected regions should see.',
  STEP_REQUIRED: 'Select at least one regulation or Cookie Notice to continue.',
}

/** Placeholder label for the Rest of the World select before a choice is made. */
export const CATCH_ALL_CHOICE_PLACEHOLDER = 'Choose an option'

/** Shown when Publish or Create is disabled because the default language is unset. */
export const DEFAULT_LOCALE_CHOICE_MESSAGE = 'Choose a default language.'

/** Shown when both the default language and Rest of the World are unset. */
export const UNSET_CONFIGURE_CHOICES_MESSAGE =
  'Choose a default language and what visitors outside your selected regions should see.'

/**
 * Descriptions for the Rest of the World select, keyed by owner value.
 * Notice has two sentences because it covers everyone when no territory is selected.
 */
export const CATCH_ALL_CHOICE_DESCRIPTION = {
  NOTICE_WITH_TERRITORY:
    'Visitors outside your selected regions see a Cookie Notice. It discloses cookies and does not block scripts.',
  NOTICE_WITHOUT_TERRITORY:
    'Every visitor sees a Cookie Notice. It discloses cookies and does not block scripts.',
  [BANNER_REGULATION.GDPR]: 'Visitors outside your selected regions see the GDPR banner.',
  [BANNER_REGULATION.CCPA]: 'Visitors outside your selected regions see the CCPA banner.',
  [CATCH_ALL_OWNER.NONE]: 'Visitors outside your selected regions see no banner.',
}

/**
 * Most-protective-first regulation priority. Used when a single regulation must
 * be picked out of a multi-regulation selection. Mirrors the widget's own fixed
 * order — the two must stay in sync.
 */
export const REGULATION_PROTECTION_ORDER = [
  BANNER_REGULATION.GDPR,
  BANNER_REGULATION.CCPA,
  BANNER_REGULATION.NOTICE,
]

/** Display modes for `RegulationBadges` — extend as new densities are needed. */
export const REGULATION_BADGES_MODE = {
  DEFAULT: 'default',
  SMALL: 'small',
}

/**
 * Regulation picker cards.
 * - `shortLabel` — compact UI (selects, chips, menus)
 * - `longLabel` — spacious UI (create cards, editor rows)
 *
 * @type {Array<{
 *   value: string,
 *   shortLabel: string,
 *   longLabel: string,
 *   fullName: string,
 *   iconSrc?: string,
 *   icon?: string,
 *   region: string,
 *   description: string,
 *   tags: string[],
 *   available: boolean,
 *   territory: string,
 * }>}
 */
export const REGULATION_CARDS = [
  {
    value: BANNER_REGULATION.GDPR,
    shortLabel: 'GDPR',
    longLabel: 'GDPR',
    fullName: 'General Data Protection Regulation',
    iconSrc: '/assets/regulation-icons/GDPR.png',
    region: 'European Union',
    description: 'Requires visitor consent before analytics and advertising cookies can be used.',
    tags: ['Prior consent required', 'Automatic script blocking', 'Granular consent controls'],
    available: true,
    territory: 'Visitors in the EU, EEA and the UK',
  },
  {
    value: BANNER_REGULATION.CCPA,
    shortLabel: 'CCPA',
    longLabel: 'CCPA',
    fullName: 'California Consumer Privacy Act',
    iconSrc: '/assets/regulation-icons/CCPA.png',
    region: 'United States of America',
    description: 'Gives US based visitors the right to opt out of the sale of their personal data.',
    tags: ['Opt-out mechanism', 'Do Not Sell link', 'Data disclosure'],
    available: true,
    territory: 'Visitors in the United States',
  },
  {
    value: BANNER_REGULATION.NOTICE,
    shortLabel: 'Notice',
    longLabel: 'Cookie Notice',
    fullName: 'Not a privacy law',
    icon: 'megaphone',
    region: 'Everywhere else',
    description:
      "Recommended for visitors your other regulations don't cover. It only discloses that your site uses cookies.",
    tags: ['Disclosure only', 'Never blocks scripts', 'Accept or close'],
    available: true,
    territory: 'Visitors no other regulation covers',
  },
  {
    value: BANNER_REGULATION.LGPD,
    shortLabel: 'LGPD',
    longLabel: 'LGPD',
    fullName: 'Lei Geral de Proteção de Dados (Brazilian General Data Protection Law)',
    iconSrc: '/assets/regulation-icons/lgpd.webp',
    region: 'Brazil',
    description:
      "Brazil's data protection law requiring lawful basis for processing personal data.",
    tags: ['Consent-based processing', 'Data subject rights', 'DPC requirement'],
    available: false,
    territory: 'Visitors in Brazil',
  },
  {
    value: BANNER_REGULATION.PIPEDA,
    shortLabel: 'PIPEDA',
    longLabel: 'PIPEDA',
    fullName: 'Personal Information Protection and Electronic Documents Act',
    iconSrc: '/assets/regulation-icons/pipdea.png',
    region: 'Canada',
    description: "Canada's federal private sector privacy law governing data collection and use.",
    tags: ['Meaningful consent', 'Accountability principle', 'Access requests'],
    available: false,
    territory: 'Visitors in Canada',
  },
]

/** Shipped regulation picker cards (`available: true`). */
export const AVAILABLE_REGULATION_CARDS = REGULATION_CARDS.filter((card) => card.available)

/** FilterColumn options for regulation multi-select (icon + label from picker cards). */
export const REGULATION_FILTER_OPTIONS = AVAILABLE_REGULATION_CARDS.map(
  ({ value, shortLabel, icon, iconSrc }) => ({
    value,
    label: shortLabel,
    icon,
    iconSrc,
  })
)

/**
 * Territory-owning available cards. Cookie Notice is excluded — it owns no region
 * of its own and can only cover catch-all / "everyone else".
 */
export const TERRITORY_REGULATION_CARDS = AVAILABLE_REGULATION_CARDS.filter(
  (card) => card.value !== BANNER_REGULATION.NOTICE
)

/** Cookie Notice card from the available set (always present while notice ships). */
export const NOTICE_REGULATION_CARD = AVAILABLE_REGULATION_CARDS.find(
  (card) => card.value === BANNER_REGULATION.NOTICE
)

/**
 * Behaviour tab guide panel — educational FAQ copy. Entry order mirrors the
 * Configure form field order, so the panel reads alongside the form rather than
 * against it.
 * @type {Array<{ question: string, answer: string, icon: string }>}
 */
export const BEHAVIOUR_GUIDE_FAQ = [
  {
    question: 'What do Regulations control?',
    icon: 'shield-check',
    answer:
      'Region your banner covers sets the consent rules visitors there receive - GDPR for the EU, EEA and UK, CCPA for the United States. A visitor in a covered region sees that regulation.',
  },
  {
    question: 'Who does Rest of the World cover?',
    icon: 'globe',
    answer:
      'Visitors outside the region your banner covers. You choose what they see: GDPR rules, CCPA rules, a Cookie Notice, or no banner. The select stays enabled, and Cookie Notice is always an option. Checking or unchecking a region leaves a still-valid choice in place. If that choice can no longer apply, it clears and you choose again. Cookie Notice is not inserted automatically. A banner saved with only GDPR still opens with GDPR rules for these visitors.',
  },
  {
    question: 'What is Google Consent Mode?',
    icon: 'sliders-horizontal',
    answer:
      'When enabled, Cookie Armor automatically adjusts Google tags based on the consent choices visitors make on your banner - so analytics and advertising tags respect opt-in or opt-out before firing.',
  },
  {
    question: 'Which languages can visitors see the banner in?',
    icon: 'languages',
    answer:
      'Visitors are shown the banner only in the languages you allow. Allowing every language also includes languages added later. When only one language is allowed, the language selector is hidden on the banner.',
  },
  {
    question: 'How does Default Language work?',
    icon: 'languages',
    answer:
      "The default language is shown when we can't determine a visitor's preferred language from their browser. Choose the language most of your audience expects.",
  },
  {
    question: 'What is the Privacy Policy URL for?',
    icon: 'file-text',
    answer:
      "We'll include a link to your privacy policy directly in the banner so visitors can read how you handle their data. The field is optional, but adding a valid URL helps meet transparency requirements.",
  },
]
